import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, X, Sparkles, Loader2 } from "lucide-react";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [message, setMessage] = useState("");
  const attemptsRef = useRef(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setMessage("Nessuna sessione di pagamento.");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 20) {
        setStatus("failed");
        setMessage("Timeout nella verifica del pagamento. Controlla la tua email.");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setMessage("Pagamento completato!");
          // refresh user data
          try {
            await api.get("/auth/me");
          } catch {}
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          setMessage("Sessione scaduta. Riprova dal pricing.");
          return;
        }
        setTimeout(poll, 2500);
      } catch (err) {
        setStatus("failed");
        setMessage(err?.response?.data?.detail || "Errore nella verifica");
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full border-2 border-zinc-800 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#FF4400] animate-spin" />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
              · Verifica in corso
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mb-3">
              Conferma del pagamento...
            </h1>
            <p className="text-zinc-400 font-body">
              Stiamo verificando con Stripe. Non chiudere la pagina.
            </p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-[#FF4400]/10 border-2 border-[#FF4400] flex items-center justify-center">
              <Check className="w-8 h-8 text-[#FF4400]" />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
              · Piano attivato
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mb-3">
              Benvenuto in {user?.plan?.toUpperCase() || "PRO"}!
            </h1>
            <p className="text-zinc-400 font-body mb-10">
              {message} Ora puoi creare reel illimitati e brand profile multipli.
            </p>
            <div className="flex gap-3 justify-center">
              <Link
                to="/dashboard"
                data-testid="payment-go-dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Vai alla dashboard
              </Link>
              <Link
                to="/brands"
                className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 text-white font-body rounded-sm transition-all"
              >
                Gestisci brand
              </Link>
            </div>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div data-testid="payment-failed">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-red-500/10 border-2 border-red-500 flex items-center justify-center">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-red-500 mb-3">
              · Pagamento non completato
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mb-3">
              Ops, qualcosa è andato storto.
            </h1>
            <p className="text-zinc-400 font-body mb-10">{message}</p>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
            >
              Torna al Pricing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
